import React, {useEffect, useState, useRef} from 'react';
import {DataGrid, GridToolbar, useGridApiRef} from '@mui/x-data-grid';
import {
    Checkbox,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Alert,
    AlertTitle,
    FormControlLabel,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import {fetchPubManagers} from '../../../../../API/AdminUserControl';
import FileDialog from '../../../../../components/dialog/FileDialog';
import {fetchExternalCompanies} from '../../../../../API/XmlFinancialTools';
import ApproveRowDialog from '../components/ApproveRowDialog';
import DeleteRowDialog from '../components/DeleteRowDialog';
import EditRowDialog from '../components/EditRowDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import useApi from '../../../../../hooks/useApi';
import {styled} from '@mui/system';
import Cookies from 'js-cookie';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 300,
        },
    },
};

const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    '& .MuiDataGrid-row': {
        '&:nth-child(even)': {
            backgroundColor: '#eaf0f5',
        },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
    },
    '& .negative.profit, .negative.profit_percentage': {
        backgroundColor: 'rgba(255,235,235)',
    },
    '& .positive.profit, .positive.profit_percentage': {
        backgroundColor: 'rgba(235,255,235)',
    },
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal !important',
        wordWrap: 'break-word',
    },
    '& .total-row': {
        backgroundColor: '#f8fff9',
        fontWeight: 'bold',
    },
}));

function processData(data) {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    return data.map(row => {
        const processedRow = { id: row.value.id, ...row.value };

        if (row.value.external) {
            processedRow.publisherDisplay = row.value.external.name;
            processedRow.publisherOriginal = row.value.external.id;
        }

        if (row.value.manager) {
            processedRow.pubManagerDisplay = row.value.manager.name;
            processedRow.pubManagerOriginal = row.value.manager.id;
        }

        if (row.value.irtb_status) {
            processedRow.irtb_statusDisplay = row.value.irtb_status.charAt(0).toUpperCase() + row.value.irtb_status.slice(1);
        }

        if (row.value.autoload === 0 || row.value.autoload === 1) {
            processedRow.autoloadDisplay = row.value.autoload === 0 ? 'No' : 'Yes';
        }

        return processedRow;
    });
}


const moreThan30DaysAgo = (dateStr) => {
    const addedAt = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - addedAt);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 30;
};


const ExternalPayoutDataGrid = ({data, onRowEdit}) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [openApprove, setOpenApprove] = useState(false);
    const [openFile, setOpenFile] = useState(false);
    const [rows, setRows] = useState(processData(data));
    const [openDelete, setOpenDelete] = useState(false);
    const [openSend, setOpenSend] = useState(false);
    const [editRow, setEditRow] = useState({});
    const [open, setOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const gridRef = useRef(null);

    const {data: sspCompany, loading: loadingCompany} = useApi(fetchExternalCompanies);

    useEffect(() => {
        setRows(processData(data));
    }, [data]);

    useEffect(() => {
        const fetchUsers = async () => {
            const response = await fetchPubManagers();
            setUsers(response);
        };

        fetchUsers();
    }, []);

    const platform = rows.some(row => 'platform' in row);
    const publisher = rows.some(row => 'external' in row);
    const hyper_status = rows.some(row => 'hyper_status' in row);
    const irtb_status = rows.some(row => 'irtb_status' in row);
    const revenue = rows.some(row => 'revenue' in row);
    const final_cost = rows.some(row => 'final_cost' in row);
    const deduction = rows.some(row => 'deduction' in row);
    const profit = rows.some(row => 'profit' in row);
    const profit_percentage = rows.some(row => 'profit_percentage' in row);
    const publisher_manager = rows.some(row => 'manager' in row);
    const revision_comment = rows.some(row => 'revision_comment' in row);
    const file = rows.some(row => 'file' in row);
    const aws_file = rows.some(row => 'aws_file' in row);
    const added_by = rows.some(row => 'added_by' in row);
    const added_at = rows.some(row => 'added_at' in row);
    const period = rows.some(row => 'period' in row);
    const cost = rows.some(row => 'cost' in row);
    const approved_by = rows.some(row => 'approved_by' in row);
    const autoloadDisplay = rows.some(row => 'autoloadDisplay' in row);

    const userAdmin = Cookies.get('user_admin');
    const userManager = Cookies.get('user_pub_manager');
    const boosOfPubManager = Cookies.get('boss_pub_managers');

    let columns = [];

    columns.push(
        {
            field: 'action',
            headerName: '',
            width: 100,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                const isOlderThan30Days = moreThan30DaysAgo(params.row.added_at);
                const canEdit = (userAdmin === '1') ||
                    (boosOfPubManager === '1' && params.row.irtb_status === 'new') ||
                    (userManager === '1' && params.row.pubManagerOriginal == localStorage.getItem('user_id') && params.row.irtb_status === 'new') &&
                    !isOlderThan30Days;
                if (params.row.period) {
                    return (
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon/>}
                            color="primary"
                            size="medium"
                            onClick={() => handleEditButtonClick(params.row)}
                            disabled={!canEdit}
                        >
                            Edit
                        </Button>
                    );
                }
            },
        }
    );

    columns.push({
        field: 'action 2',
        headerName: '',
        width: 130,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
            const isOlderThan30Days = moreThan30DaysAgo(params.row.added_at);
            const canEdit = (userAdmin === '1') ||
                (boosOfPubManager === '1' && params.row.irtb_status === 'new') ||
                (userManager === '1' && params.row.pubManagerOriginal == localStorage.getItem('user_id') && params.row.irtb_status === 'new') &&
                !isOlderThan30Days;
            if (params.row.period) {
                return (
                    <Button
                        variant="outlined"
                        startIcon={<AssignmentTurnedInIcon/>}
                        color="success"
                        size="medium"
                        onClick={() => handleApproveButtonClick(params.row)}
                        disabled={!canEdit}
                    >
                        Approve
                    </Button>
                );
            }
        },
    });

    columns.push({
        field: 'action 3',
        headerName: '',
        width: 130,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
            const canEdit = (userAdmin === '1') ||
                (boosOfPubManager === '1' && params.row.irtb_status === 'new') ||
                (userManager === '1' && params.row.pubManagerOriginal == localStorage.getItem('user_id') && params.row.irtb_status === 'new');
            if (params.row.period) {
                return (
                    <Button
                        variant="outlined"
                        startIcon={<DeleteIcon/>}
                        color="error"
                        size="medium"
                        onClick={() => handleDeleteButtonClick(params.row)}
                        disabled={!canEdit}
                    >
                        Delete
                    </Button>
                );
            }
        },
    });

    columns.push({field: 'id', headerName: 'ID', width: 80});

    if (rows.some(row => row.period)) {
        columns.push({ field: 'period', headerName: 'Period', width: 80 });
    }

    if (rows.some(row => row.publisherDisplay)) {
        columns.push({ field: 'publisherDisplay', headerName: 'Platform', width: 250 });
    }

    if (rows.some(row => row.platformDisplay)) {
        columns.push({ field: 'platformDisplay', headerName: 'Platform', width: 100 });
    }

    if (rows.some(row => row.hyper_status)) {
        columns.push({ field: 'hyper_status', headerName: 'Hyper Status', width: 150 });
    }

    if (rows.some(row => row.irtb_statusDisplay)) {
        columns.push({ field: 'irtb_statusDisplay', headerName: 'iRTB Status', width: 150 });
    }

    if (rows.some(row => row.cost)) {
        columns.push({ field: 'cost', headerName: 'Cost, $', width: 180 });
    }

    if (rows.some(row => row.final_cost)) {
        columns.push({ field: 'final_cost', headerName: 'Final Cost, $', width: 100 });
    }

    if (rows.some(row => row.profit)) {
        columns.push({ field: 'profit', headerName: 'Profit, $', width: 100 });
    }

    if (rows.some(row => row.profit_percentage)) {
        columns.push({ field: 'profit_percentage', headerName: 'Profit, %', width: 100 });
    }

    if (rows.some(row => row.revenue)) {
        columns.push({ field: 'revenue', headerName: 'Revenue, $', width: 100 });
    }

    if (rows.some(row => row.deduction)) {
        columns.push({ field: 'deduction', headerName: 'Deduction $', width: 100 });
    }

    if (rows.some(row => row.file || row.aws_file)) {
        columns.push({
            field: 'action 4',
            headerName: 'File',
            width: 130,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                const canEdit = params.row.file || params.row.aws_file;
                if (params.row.period) {
                    return (
                        <Button
                            variant="outlined"
                            startIcon={<FileCopyOutlinedIcon />}
                            color="primary"
                            size="medium"
                            onClick={() => handleFileButtonClick(params.row)}
                            disabled={!canEdit}
                        >
                            File
                        </Button>
                    );
                }
            },
        });
    }

    if (rows.some(row => row.pubManagerDisplay)) {
        columns.push({ field: 'pubManagerDisplay', headerName: 'Platform manager', width: 150 });
    }

    if (rows.some(row => row.autoloadDisplay)) {
        columns.push({ field: 'autoloadDisplay', headerName: 'Autoload', width: 150 });
    }

    if (rows.some(row => row.added_by)) {
        columns.push({ field: 'added_by', headerName: 'Added by', width: 150 });
    }

    if (rows.some(row => row.added_at)) {
        columns.push({ field: 'added_at', headerName: 'Added at', width: 150 });
    }

    if (rows.some(row => row.revision_comment)) {
        columns.push({ field: 'revision_comment', headerName: 'Revision comment', width: 150 });
    }

    if (rows.some(row => row.approved_by)) {
        columns.push({ field: 'approved_by', headerName: 'Approved by', width: 150 });
    }

    columns = columns.map((col) => {
        if (['profit', 'profit_percentage'].includes(col.field)) {
            return {
                ...col,
                cellClassName: (params) => {
                    const value = params.value;
                    if (value < 0) {
                        return `negative ${col.field}`;
                    }
                },
            };
        }
        return col;
    });


    const handleEditButtonClick = (row) => {
        setEditRow(row);
        setOpen(true);
    };

    const handleDeleteButtonClick = (row) => {
        setEditRow(row);
        setOpenDelete(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteClose = () => {
        setOpenDelete(false);
    };

    const handleCloseSend = () => {
        setOpenSend(false);
    };

    const openSendSuccess = () => {
        setOpenSend(true);
    };

    const handleApproveClose = () => {
        setOpenApprove(false);
    };

    const handleApproveButtonClick = (row) => {
        setEditRow(row);
        setOpenApprove(true);
    };

    const handleFileButtonClick = (row) => {
        setEditRow(row);
        setOpenFile(true);
    };

    const handleFileClose = () => {
        setOpenFile(false);
    };

    const handleRowDelete = (deletedRowId) => {
        setRows(rows.filter(row => row.id !== deletedRowId));
    };

    const handleRowEdit = () => {
        onRowEdit();
    };

    if (data.length === 0) {
        return null;
    } else {

        return (

            <div style={{height: 800, width: '100%'}} ref={gridRef}>
                <StyledDataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={25}
                    slots={{
                        toolbar: GridToolbar,
                    }}
                    disableRowSelectionOnClick
                />

                <EditRowDialog
                    open={open}
                    handleClose={handleClose}
                    ssp={sspCompany}
                    editRow={editRow}
                    onSubmitSuccess={handleRowEdit}
                />

                <DeleteRowDialog
                    open={openDelete}
                    handleClose={handleDeleteClose}
                    editRow={editRow}
                    onDeleteSuccess={handleRowDelete}
                />

                <ApproveRowDialog
                    open={openApprove}
                    handleClose={handleApproveClose}
                    editRow={editRow}
                    onApproveSuccess={handleRowEdit}
                />

                <FileDialog
                    open={openFile}
                    handleClose={handleFileClose}
                    firstLink={editRow.file || ''}
                    secondLink={editRow.aws_file || ''}
                />
            </div>
        );
    }
};

export default ExternalPayoutDataGrid;
